import React, { Component } from 'react';
import noop from 'lodash/noop';
import isEmpty from 'lodash/isEmpty';
import { gettext } from '@eb/i18n';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';

import SocialLoginButtons from './SocialLoginButtons';

import {
    SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK,
    SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE,
    SOCIAL_EXTERNAL_IDENTITY_PROVIDER_GOOGLE,
} from './constants';

import './SocialLogin.scss';

export default class SocialLogin extends Component {
    static propTypes = {
        children: PropTypes.node,
        getButtonsOnly: PropTypes.bool,
        fp: PropTypes.bool,
        isCheckoutAuthenticationFlow: PropTypes.bool,
        socialSSOSettings: PropTypes.shape({
            [SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK]: PropTypes.shape({
                appId: PropTypes.string,
                version: PropTypes.string,
                scope: PropTypes.string,
                reRequestPermissions: PropTypes.bool,
            }),
            [SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE]: PropTypes.shape({
                clientId: PropTypes.string.isRequired,
                redirectURI: PropTypes.string.isRequired,
                scope: PropTypes.string,
                state: PropTypes.string,
            }),
            [SOCIAL_EXTERNAL_IDENTITY_PROVIDER_GOOGLE]: PropTypes.shape({
                clientId: PropTypes.string.isRequired,
                redirectURI: PropTypes.string,
                scope: PropTypes.string,
                state: PropTypes.string,
            }),
        }),
        onSocialConnectVerified: PropTypes.func,
        onSocialConnectNotAuthorized: PropTypes.func,
        handleOnLoadComplete: PropTypes.func,
        handleOnComponentUpdated: PropTypes.func,
        onNotConnected: PropTypes.func,
        onSocialProviderClick: PropTypes.func,
        onTermsOfServiceClick: PropTypes.func,
        onComunityGuidelinesClick: PropTypes.func,
        onPrivacyPolicyClick: PropTypes.func,
    };

    static defaultProps = {
        onSocialProviderClick: noop,
        onSocialConnectVerified: noop,
        onSocialConnectNotAuthorized: noop,
    };

    constructor(props) {
        super(props);

        const {
            onTermsOfServiceClick,
            onComunityGuidelinesClick,
            onPrivacyPolicyClick,
        } = this.props;

        this.privacyPolicyNameToHandler = {
            tos: onTermsOfServiceClick,
            'community-guidelines': onComunityGuidelinesClick,
            'privacy-policy': onPrivacyPolicyClick,
        };
    }

    _handleSocialProviderClick = (socialProvider, state) => {
        const { fp, onSocialProviderClick } = this.props;

        if (fp) {
            const DeviceFingerprint = loadable((props) =>
                import('@eb/device-fingerprint'),
            );

            DeviceFingerprint.load().then((component) => {
                component.create({ data: { fpid: '' } });

                onSocialProviderClick(socialProvider, state);
            });
        }
    };

    _handlePrivacyPolicyClick = (e) => {
        const targetName = e.target.name;

        if (targetName) {
            const privacyPolicyHandler = this.privacyPolicyNameToHandler[
                targetName
            ];

            if (privacyPolicyHandler) {
                privacyPolicyHandler();
            }
        }
    };

    render() {
        const {
            children,
            socialSSOSettings,
            getButtonsOnly,
            isCheckoutAuthenticationFlow,
            onSocialConnectVerified,
            onSocialConnectNotAuthorized,
            onSocialLogInUserAuthorized,
            handleOnLoadComplete,
            handleOnComponentUpdated,
            onNotConnected,
        } = this.props;
        const socialButtons = (
            <SocialLoginButtons
                isCheckoutAuthenticationFlow={isCheckoutAuthenticationFlow}
                handleOnLoadComplete={handleOnLoadComplete}
                handleOnComponentUpdated={handleOnComponentUpdated}
                onNotConnected={onNotConnected}
                socialSSOSettings={socialSSOSettings}
                onSocialConnectVerified={onSocialConnectVerified}
                onSocialConnectNotAuthorized={onSocialConnectNotAuthorized}
                onSocialLogInUserAuthorized={onSocialLogInUserAuthorized}
                handleSocialProviderClick={this._handleSocialProviderClick}
            />
        );
        let divider = null;
        let privacyPolicy = null;
        let privacyTerms = gettext(
            'By clicking "Get Started" or "Continue with Facebook", I accept the Eventbrite <a href="/tos" class="eds-link" target="_blank" name="tos" rel="noopener noreferrer">terms of service</a>, <a href="/community-guidelines" class="eds-link" name="community-guidelines" target="_blank" rel="noopener noreferrer">community guidelines</a> and have read the <a href="/privacypolicy" class="eds-link" name="privacy-policy" target="_blank" rel="noopener noreferrer">privacy policy</a>.',
        );
        const twoStepsSignup =
            socialSSOSettings && socialSSOSettings.twoStepsSignup;

        if (getButtonsOnly) {
            return <div>{socialButtons}</div>;
        }

        if (!isEmpty(socialSSOSettings)) {
            divider = (
                <div className="eds-l-mar-vert-4 eds-text--center">
                    <span className="eds-text-bs">{gettext('or')}</span>
                </div>
            );
            if (socialSSOSettings['apple']) {
                privacyTerms = gettext(
                    'By clicking “Get Started”, “Continue with Apple” or “Continue with Facebook”, I accept the Eventbrite <a href="/tos" class="eds-link" target="_blank" name="tos" rel="noopener noreferrer">terms of service</a>, <a href="/community-guidelines" class="eds-link" name="community-guidelines" target="_blank" rel="noopener noreferrer">community guidelines</a> and have read the <a href="/privacypolicy" class="eds-link" name="privacy-policy" target="_blank" rel="noopener noreferrer">privacy policy</a>.',
                );
            }
            if (!twoStepsSignup) {
                privacyPolicy = (
                    <div className="privacy-terms eds-l-mar-top-3">
                        <div className="eds-text--center">
                            <span
                                className="eds-text-color--grey-600 eds-text-bs eds-l-mar-bot-5"
                                onClick={this._handlePrivacyPolicyClick}
                                onKeyDown={this._handlePrivacyPolicyClick}
                                role="presentation"
                                // Needed for links to work in gettext; no user-generated content
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{
                                    __html: privacyTerms.toString(),
                                }}
                            />
                        </div>
                    </div>
                );
            }
        }

        return (
            <div>
                {children}
                {divider}
                {socialButtons}
                {privacyPolicy}
            </div>
        );
    }
}
